// Slide order is presentation; UUIDs are durable identities. Top-level traversal
// leaves callout children attached to their parent, as in Handbook::Document.
export const slides = document => document.slides
export const flattenBlocks = document => slides(document).flatMap(slide => slide.blocks)
export const findSlide = (document, id) => slides(document).find(slide => slide.id === id)
export const slideContainingBlock = (document, id) => slides(document).find(slide => slide.blocks.some(block => block.id === id || block.children?.some(child => child.id === id)))
export const newSlide = () => ({ id: crypto.randomUUID(), blocks: [] })
export function duplicateBlock(block) {
  const copy = JSON.parse(JSON.stringify(block))
  copy.id = crypto.randomUUID()
  if (copy.type === "heading") copy.anchor = ""
  if (copy.children) copy.children = copy.children.map(duplicateBlock)
  return copy
}
export const duplicateSlide = slide => ({ id: crypto.randomUUID(), blocks: slide.blocks.map(duplicateBlock) })

export const blockLabels = { paragraph: "Akapit", heading: "Nagłówek", list: "Lista", remember: "Zapamiętaj", warning: "Uwaga", image: "Obraz" }
const whitespace = /[\u0009-\u000d\u0020\u0085\u00a0\u1680\u2000-\u200a\u2028\u2029\u202f\u205f\u3000]+/u
export function readingMetrics({ title = "", h1 = "", lead = "", content }) {
  const texts = [h1.trim() ? h1 : title, lead, ...bodyTexts(content)]
  const words = texts.filter(text => typeof text === "string").reduce((sum, text) => sum + text.split(whitespace).filter(token => /[\p{L}\p{N}]/u.test(token)).length, 0)
  return { words, minutes: Math.ceil(words / 180) }
}
export function newBlock(type) {
  if (!Object.hasOwn(blockLabels, type)) throw new Error("Nieobsługiwany typ bloku")
  const block = { id: crypto.randomUUID(), type }
  if (type === "image") return block
  if (["remember", "warning"].includes(type)) return { ...block, children: [newChild("paragraph")] }
  return type === "list" ? { ...block, ordered: false, items: [""] } : { ...block, ...(type === "heading" ? { level: 2, anchor: "" } : {}), text: "" }
}

export function effectiveMetadata({ title = "", h1 = "", lead = "", html_title = "", meta_description = "", content }) {
  const effectiveH1 = h1.trim() ? h1 : title
  const firstText = bodyTexts(content).find(text => text?.trim()) || ""
  const summary = (lead.trim() ? lead : firstText).split(whitespace).filter(Boolean).join(" ")
  const characters = [...summary]
  let description = summary
  if (characters.length > 160) {
    const start = characters.slice(0, 158).join("")
    const boundary = start.lastIndexOf(" ")
    description = characters.slice(0, boundary >= 0 ? [...start.slice(0, boundary)].length : 157).join("") + "..."
  }
  return { h1: effectiveH1, title: html_title.trim() ? html_title : `${effectiveH1} — Podręcznik karty rowerowej`, description: meta_description.trim() ? meta_description.trim().replace(/\s+/gu, " ") : description }
}


export const childLabels = { paragraph: "Akapit", subheading: "Śródtytuł", list: "Lista" }
export function inlineText(value) {
  return typeof value === "string" ? value : value.segments ? value.segments.map(segment => segment.text).join("") : value.text
}
export function bodyTexts(content) {
  const text = block => {
    if (block.type === "image") return []
    if (block.type === "list") return block.items.map(inlineText)
    if (["remember", "warning"].includes(block.type) && block.children) return block.children.flatMap(text)
    return [inlineText(block)]
  }
  return flattenBlocks(content).flatMap(text)
}
export function newChild(type) {
  if (!Object.hasOwn(childLabels, type)) throw new Error("Nieobsługiwany element calloutu")
  return type === "subheading" ? { id: crypto.randomUUID(), type, text: "" } : newBlock(type)
}
export function prepareEditor(content) {
  for (const block of flattenBlocks(content)) {
    if (["remember", "warning"].includes(block.type) && Object.hasOwn(block, "text") && !Object.hasOwn(block, "children")) {
      block.children = [{ id: crypto.randomUUID(), type: "paragraph", text: block.text }]
      delete block.text
    }
  }
  return content
}
export function serializableDocument(content) {
  const copy = JSON.parse(JSON.stringify(content))
  const cleanInline = value => { if (Array.isArray(value?.segments)) value.segments = value.segments.filter(segment => segment.text !== "") }
  const cleanBlock = block => {
    cleanInline(block)
    if (block.type === "list") block.items.forEach(cleanInline)
  }
  for (const block of flattenBlocks(copy)) {
    cleanBlock(block)
    if (["remember", "warning"].includes(block.type) && Array.isArray(block.children)) block.children.forEach(cleanBlock)
  }
  return copy
}
// selectionStart/End and slice both use UTF-16 offsets, including Polish and emoji text.
export function strongSelection(segments, index, start, end) {
  const segment = segments[index]
  if (Object.hasOwn(segment, "href")) return segments
  if (start >= end || start < 0 || end > segment.text.length) return segments
  const replacement = [
    { ...segment, text: segment.text.slice(0, start) },
    { text: segment.text.slice(start, end), strong: true },
    { ...segment, text: segment.text.slice(end) }
  ].filter(part => part.text !== "")
  return [...segments.slice(0, index), ...replacement, ...segments.slice(index + 1)]
}


// One selection in one plain field becomes one link. Never combine formatting.
export function linkSelection(segments, index, start, end, href) {
  const segment = segments[index]
  if (segment.strong || Object.hasOwn(segment, "href") || start >= end || start < 0 || end > segment.text.length) return segments
  const replacement = [
    { text: segment.text.slice(0, start) },
    { text: segment.text.slice(start, end), href },
    { text: segment.text.slice(end) }
  ].filter(part => part.text !== "")
  return [...segments.slice(0, index), ...replacement, ...segments.slice(index + 1)]
}
